import { useEffect, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getService } from "@/api";
import { useSeo, faqSchema } from "@/seo";
import { Reveal, SectionHead, Eyebrow } from "@/components/Reveal";
import { CalendlyCta } from "@/components/SiteChrome";

const LIFECYCLE = [
  { step: "Plan", body: "Design pricing policy, the operating model and the intercompany framework before transactions happen." },
  { step: "Document", body: "Build the Master File, Local File, Disclosure Form and benchmarking that evidence it." },
  { step: "Defend", body: "Stand behind the file in FTA queries, audits, APAs and disputes." },
];

// Five service categories, content drawn from NexusPrice (a TruVerse practice).
const CATEGORIES = [
  {
    title: "Transfer Pricing Advisory",
    body: "Strategic design of transfer pricing models, value-chain alignment, transaction structuring and policy formulation, so profit sits where value is actually created.",
    points: [
      "TP impact and risk assessment across the group structure and flows",
      "Related-party and connected-person mapping under Articles 35 36",
      "TP policy design and intercompany pricing models",
      "Value chain and operating model review (DEMPE)",
      "Free Zone (QFZP) TP health checks to protect the 0% rate",
    ],
  },
  {
    title: "Economic Analysis & Benchmarking",
    body: "Functional, asset and risk (FAR) analysis and precise arm's-length range determination using global comparable data and accepted OECD methodologies.",
    points: [
      "Functional, asset and risk (FAR) analysis to choose the tested party",
      "Benchmarking and comparability studies with interquartile ranges",
      "Method selection and justification (CUP, RPM, Cost Plus, TNMM, Profit Split)",
      "Connected-person remuneration benchmarking for deductibility",
      "Annual refresh of comparables as the business changes",
    ],
  },
  {
    title: "Compliance & Documentation",
    body: "Jurisdiction-specific compliance, Local File, Master File, TP Disclosure Form and CbC reporting, aligned with OECD guidance and UAE FTA requirements.",
    points: [
      "TP Disclosure Form schedules filed with the CT return",
      "Local File, entity-level and ready within 30 days of an FTA request",
      "Master File, coordinated with head office and other jurisdictions",
      "Country-by-Country Reporting (CbCR) for qualifying MNE groups",
      "Threshold and exemption assessment, including Small Business Relief",
    ],
  },
  {
    title: "TP Litigation & Representation",
    body: "End-to-end support in audits, defence strategy, dispute resolution and representation before tax authorities, including MAP and APA proceedings.",
    points: [
      "FTA audit defence files and responses to information requests",
      "Reconsideration requests and appeals on TP adjustments",
      "Advance Pricing Agreements (UAPA) feasibility and application",
      "Mutual Agreement Procedure (MAP) and double-tax relief",
      "Intercompany agreement drafting to match legal form to substance",
    ],
  },
  {
    title: "Technology & Automation",
    body: "Automation and analytics that make transfer pricing accurate, efficient and repeatable, from data extraction to year-end true-ups in your ERP.",
    points: [
      "Operational TP, turning policy into invoices and monitoring margins",
      "Year-end true-up calculations booked correctly in the ERP",
      "TP manuals, governance and team training",
      "Data extraction and analytics for benchmarking and reporting",
      "Pillar Two / DMTT data collection and workings",
    ],
  },
];

const WHO = [
  "Qualifying Free Zone Persons protecting the 0% rate",
  "UAE subsidiaries and regional HQs of multinational groups",
  "Indian groups with Dubai holding or trading companies",
  "Family-owned groups with mainland and free-zone entities",
  "Groups in scope of Pillar Two",
];

const THRESHOLDS = [
  { obligation: "Arm's-length pricing", trigger: "Any transaction with a related party or connected person", timing: "Always" },
  { obligation: "Disclosure Form, related parties", trigger: "Related-party transactions above AED 40m (categories above AED 4m listed)", timing: "With the CT return" },
  { obligation: "Disclosure Form, connected persons", trigger: "Payments above AED 500,000 to one connected person", timing: "With the CT return" },
  { obligation: "Master File + Local File", trigger: "Entity revenue of AED 200m+ or group revenue of AED 3.15bn+", timing: "Within 30 days of FTA request" },
  { obligation: "Country-by-Country Report", trigger: "UAE-parented MNE groups with revenue of AED 3.15bn+", timing: "12 months after year-end" },
];

const FAQS = [
  { q: "Who must follow UAE transfer pricing rules?", a: "Any business that transacts with a related party or connected person, domestic or cross-border, must price those dealings at arm's length. There is no size exemption from the principle itself." },
  { q: "What is the arm's-length principle?", a: "It requires related-party transactions to be priced as if agreed between independent parties in comparable circumstances. It is the foundation of UAE and OECD transfer pricing rules." },
  { q: "When is a Master File required?", a: "Where entity revenue is AED 200m or more, or the MNE group revenue is AED 3.15bn or more, both a Master File and a Local File must be maintained and produced to the FTA within 30 days of request." },
  { q: "Do free-zone companies need transfer pricing documentation?", a: "Yes. Free-zone entities, including Qualifying Free Zone Persons, remain subject to the transfer pricing rules, and TP compliance is a condition of keeping the 0% free-zone rate." },
  { q: "Which TP methods does the FTA accept?", a: "The five OECD methods: comparable uncontrolled price, resale price, cost plus, transactional net margin and profit split. We select and justify the most appropriate method per transaction." },
  { q: "What happens in an FTA audit?", a: "The FTA may request your TP documentation, FAR analysis and intercompany agreements, often on short timelines. A prepared file, consistent pricing and a clear defence strategy materially reduce adjustment risk." },
  { q: "What is the TP Disclosure Form and when is it due?", a: "It is a schedule of related-party and connected-person transactions filed with the Corporate Tax return where related-party transactions exceed AED 40 million in aggregate (categories above AED 4 million listed separately). It is due with the return, 9 months after year-end." },
  { q: "Do domestic (UAE-to-UAE) transactions need transfer pricing?", a: "Yes. The arm's-length rule applies to domestic related-party dealings as well as cross-border ones, including transactions between mainland and free-zone entities." },
  { q: "Is there an exemption for small businesses?", a: "Businesses claiming Small Business Relief are exempt from TP documentation, but not from the arm's-length principle itself. The obligation to price related-party dealings at arm's length still applies." },
  { q: "What is an Advance Pricing Agreement and should we apply?", a: "An APA is an advance agreement with the FTA on the transfer pricing method for specific transactions, giving certainty for a fixed period. The FTA opened unilateral APAs for domestic transactions, with cross-border UAPAs to follow; we assess feasibility and manage the application." },
];

export default function ServiceTax() {
  const [service, setService] = useState(null);
  useSeo({
    title: "Transfer Pricing Consultants Dubai | UAE TP Advisory, TruVerse",
    description:
      "Transfer pricing at arm's length, value aligned. Advisory, benchmarking, Master & Local File documentation and FTA audit defence for UAE and India UAE groups.",
    schema: faqSchema(FAQS),
  });
  useEffect(() => {
    getService("international-tax-transfer-pricing").then(setService).catch(() => setService({}));
  }, []);

  if (!service) return <div className="container-site py-32 text-muted-foreground" data-testid="service-tax-loading">Loading…</div>;

  return (
    <div data-testid="service-tax-page">
      <section className="bg-ink py-24 lg:py-32">
        <div className="container-site max-w-4xl">
          <Reveal>
            <h1 className="mt-2 text-4xl font-bold leading-tight tracking-tight text-ink-fg md:text-5xl">
              Transfer Pricing at arm's length.
              <span className="block">Value aligned, globally delivered.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-muted md:text-lg">
              UAE corporate tax and OECD-aligned transfer pricing rules now apply to every group with related-party
              transactions. We help you price, document and defend them, in the UAE and across your other markets.
            </p>
            <div className="mt-10">
              <CalendlyCta label="Get a transfer pricing health check" testId="tax-cta-book-btn" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24" data-testid="tax-who-section">
        <div className="container-site grid gap-12 lg:grid-cols-2">
          <Reveal>
            <SectionHead eyebrow="Who it's for" title="Groups the new rules already touch." />
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="space-y-4">
              {WHO.map((w) => (
                <li key={w} className="flex items-start gap-4 border-b border-border pb-4 text-base text-foreground" data-testid="tax-who-item">
                  <span className="mt-2 h-px w-6 shrink-0 bg-accent" aria-hidden="true" />
                  {w}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="bg-secondary/60 py-24" data-testid="tax-lifecycle-section">
        <div className="container-site">
          <Reveal>
            <SectionHead eyebrow="The lifecycle" title="Plan. Document. Defend." />
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {LIFECYCLE.map((l, i) => (
              <Reveal key={l.step} delay={i * 0.1}>
                <div className="border-t-2 border-accent pt-8" data-testid={`tax-lifecycle-${l.step.toLowerCase()}`}>
                  <span className="text-5xl font-bold text-accent/25">{i + 1}</span>
                  <h3 className="mt-3 text-2xl font-bold text-foreground">{l.step}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{l.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="tax-services-section">
        <div className="container-site grid gap-12 lg:grid-cols-3">
          <Reveal>
            <SectionHead
              eyebrow="What we deliver"
              title="End-to-end, across the full TP cycle."
              intro="We deliver end-to-end transfer pricing across regions, from model design and benchmarking to global documentation, combining deep knowledge of local tax law with one consistent approach."
            />
            <div className="mt-10 grid grid-cols-3 gap-px border border-border bg-border" data-testid="tax-key-facts-panel">
              <div className="bg-card p-5">
                <p className="text-2xl font-bold text-foreground md:text-3xl">9%</p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground">UAE corporate tax</p>
                <p className="mt-1 text-[11px] leading-snug text-muted-foreground">On taxable income above AED 375,000</p>
              </div>
              <div className="bg-card p-5">
                <p className="text-2xl font-bold text-foreground md:text-3xl">AED 200m</p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground">Master / Local File</p>
                <p className="mt-1 text-[11px] leading-snug text-muted-foreground">Entity revenue, or group AED 3.15bn+</p>
              </div>
              <div className="bg-card p-5">
                <p className="text-2xl font-bold text-foreground md:text-3xl">15%</p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground">Global minimum tax</p>
                <p className="mt-1 text-[11px] leading-snug text-muted-foreground">For large MNE groups (Pillar Two)</p>
              </div>
            </div>
            <p className="mt-3 text-[11px] italic leading-relaxed text-muted-foreground">
              Figures for guidance only; confirm against current FTA rules.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-2">
            <Accordion type="single" collapsible className="w-full" data-testid="tax-services-accordion">
              {CATEGORIES.map((s, i) => (
                <AccordionItem key={s.title} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-lg font-semibold text-foreground" data-testid={`tax-accordion-trigger-${i}`}>
                    {s.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                    <p>{s.body}</p>
                    <ul className="mt-4 space-y-2">
                      {s.points.map((p) => (
                        <li key={p} className="flex items-start gap-3">
                          <span className="mt-2 h-px w-4 shrink-0 bg-accent" aria-hidden="true" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="bg-secondary/60 py-24" data-testid="tax-thresholds-section">
        <div className="container-site">
          <Reveal>
            <SectionHead eyebrow="Who needs what" title="UAE transfer pricing obligations at a glance." />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-14 overflow-x-auto border border-border bg-card" data-testid="tax-thresholds-table">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead>
                  <tr className="border-b border-border text-xs uppercase tracking-[0.15em] text-muted-foreground">
                    <th className="p-5 font-semibold">Obligation</th>
                    <th className="p-5 font-semibold">Trigger</th>
                    <th className="p-5 font-semibold">Timing</th>
                  </tr>
                </thead>
                <tbody>
                  {THRESHOLDS.map((t) => (
                    <tr key={t.obligation} className="border-b border-border last:border-0">
                      <td className="p-5 font-semibold text-foreground">{t.obligation}</td>
                      <td className="p-5 text-muted-foreground">{t.trigger}</td>
                      <td className="p-5 text-muted-foreground">{t.timing}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-[11px] italic text-muted-foreground" data-testid="tax-thresholds-note">
              Based on Ministerial Decision No. 97 of 2023 and the FTA Transfer Pricing Guide. Indicative, confirm against current FTA rules.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24" data-testid="tax-faq-section">
        <div className="container-site max-w-3xl">
          <Reveal>
            <SectionHead eyebrow="FAQs" title="Asked in every first meeting." />
            <Accordion type="single" collapsible className="mt-10 w-full" data-testid="tax-faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-medium text-foreground" data-testid={`tax-faq-trigger-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-20" data-testid="tax-final-cta">
        <div className="container-site flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <h2 className="max-w-xl text-3xl font-bold tracking-tight text-ink-fg md:text-4xl">Get a transfer pricing health check.</h2>
          <CalendlyCta label="Book a consultation" testId="tax-bottom-book-btn" />
        </div>
      </section>
    </div>
  );
}
